import { $getRoot, $createParagraphNode, $createTextNode } from "lexical";
import { useEffect } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import PropTypes from "prop-types";

const theme = {
  // Theme styling goes here
};

// Plugin to set initial content
function InitialContentPlugin({ content }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (content) {
      editor.update(() => {
        const root = $getRoot();
        root.clear();

        // Split content by newlines and create paragraph nodes
        const lines = content.split("\n");
        lines.forEach((line) => {
          const paragraphNode = $createParagraphNode();
          if (line) {
            const textNode = $createTextNode(line);
            paragraphNode.append(textNode);
          }
          root.append(paragraphNode);
        });
      });
    }
  }, [content, editor]);

  return null;
}

InitialContentPlugin.propTypes = {
  content: PropTypes.string,
};

function EditorContent({
  initialContent,
  onChange,
  placeholder,
  autoFocus,
  ariaLabelledBy,
}) {
  function handleChange(editorState) {
    editorState.read(() => {
      const root = $getRoot();
      const children = root.getChildren();
      const markdown = children
        .map((child) => child.getTextContent())
        .join("\n");
      onChange(markdown);
    });
  }

  return (
    <>
      <RichTextPlugin
        contentEditable={
          <ContentEditable
            aria-labelledby={ariaLabelledBy}
            aria-placeholder={placeholder}
            placeholder={
              <div
                className="absolute text-muted-foreground"
                style={{ top: "8px", left: "12px", pointerEvents: "none" }}
              >
                {placeholder}
              </div>
            }
            style={{
              minHeight: "100px",
              outline: "none",
              padding: "8px 12px",
              position: "relative",
            }}
            className="w-full rounded-md border border-input bg-transparent text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring md:text-sm dark:bg-input/30"
          />
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <OnChangePlugin onChange={handleChange} />
      {autoFocus && <AutoFocusPlugin />}
      <HistoryPlugin />
      <InitialContentPlugin content={initialContent} />
    </>
  );
}

EditorContent.propTypes = {
  initialContent: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  ariaLabelledBy: PropTypes.string,
};

const RichTextEditor = ({
  initialContent,
  onChange,
  placeholder = "Enter text...",
  autoFocus = false,
  ariaLabelledBy = undefined,
}) => {
  const initialConfig = {
    namespace: "RichTextEditor",
    theme,
    onError: (error) => console.error(error),
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <EditorContent
        initialContent={initialContent}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        ariaLabelledBy={ariaLabelledBy}
      />
    </LexicalComposer>
  );
};

RichTextEditor.propTypes = {
  initialContent: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  // Lexical's AutoFocusPlugin: a dialog focuses its first editor on open.
  autoFocus: PropTypes.bool,
  ariaLabelledBy: PropTypes.string,
};

export default RichTextEditor;
